import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getSavedLessonPassword,
  saveLessonPassword,
  clearLessonPassword,
  fetchLessonPatterns,
} from '../lib/lesson-access';

/**
 * Who is signed in for the lesson patterns, shared by the header menu and the guitar page.
 * Wrap the app in <LessonAccessProvider> (src/index.js) and read it with useLessonAccess():
 *
 *   { signedIn, patterns, signIn(password), signOut() }
 *
 * `patterns` is the server's data while signed in, else null. `signIn` resolves on
 * success and rejects with the Error from `fetchLessonPatterns` otherwise. On load, a
 * saved password is tried once; if the server now refuses it, it is forgotten.
 */
const LessonAccessContext = createContext({
  signedIn: false,
  patterns: null,
  signIn: async () => { throw Object.assign(new Error('network'), { reason: 'network' }); },
  signOut: () => {},
});

export function LessonAccessProvider({ children }) {
  const [patterns, setPatterns] = useState(null);

  useEffect(() => {
    const saved = getSavedLessonPassword();
    if (!saved) return undefined;
    let cancelled = false;
    fetchLessonPatterns(saved).then(
      (data) => { if (!cancelled) setPatterns(data); },
      (err) => { if (err.reason === 'wrong_password') clearLessonPassword(); },
    );
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (password) => {
    const data = await fetchLessonPatterns(password);
    saveLessonPassword(password);
    setPatterns(data);
  }, []);

  const signOut = useCallback(() => {
    clearLessonPassword();
    setPatterns(null);
  }, []);

  const value = useMemo(
    () => ({ signedIn: patterns !== null, patterns, signIn, signOut }),
    [patterns, signIn, signOut],
  );

  return <LessonAccessContext.Provider value={value}>{children}</LessonAccessContext.Provider>;
}

export function useLessonAccess() {
  return useContext(LessonAccessContext);
}
